import i18n from '@/i18n';

// i18n.language je uvijek goli kod ('hr'/'en'/'de', vidi load: 'languageOnly'
// u src/i18n/index.ts) — Intl treba puni locale (regija utječe na poredak
// dana/mjeseca, separator, 12/24h sat...). en/de nemaju drugi signal u
// projektu za odabir regije, pa je en-GB/de-DE razuman, EU-dosljedan default.
const INTL_LOCALES: Record<string, string> = {
  hr: 'hr-HR',
  de: 'de-DE',
  en: 'en-GB'
};

export const currentLocale = () => INTL_LOCALES[i18n.language] ?? INTL_LOCALES.en;

export const formatDate = (iso?: string) =>
  iso ? new Date(iso).toLocaleDateString(currentLocale()) : '—';

export const formatDateTime = (iso?: string) =>
  iso
    ? `${new Date(iso).toLocaleDateString(currentLocale())} ${new Date(iso).toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' })}`
    : undefined;

/** Isto kao formatDateTime, ali datum/vrijeme odvojeno — za prikaz u dva reda. */
export const formatDateTimeParts = (iso: string) => {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString(currentLocale()),
    time: d.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' })
  };
};

export const formatRole = (role?: string) =>
  role
    ? role
        .toLowerCase()
        .split('_')
        .join(' ')
        .replace(/^./, (char) => char.toUpperCase())
    : '—';

export const formatAmount = (value?: number) =>
  `${(value ?? 0).toLocaleString(currentLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;

/** Normativi sati i sl. — format jezika (hr/de 18,2; en 18.2), do 2 decimale; prazno = '—'. */
export const formatNumber = (value?: number | null) =>
  value == null ? '—' : value.toLocaleString(currentLocale(), { maximumFractionDigits: 2 });

export const otherPartyName = (
  entity: { partnerOrgName?: string; clientOrgName?: string },
  viewerOrgName?: string
) =>
  entity.partnerOrgName === viewerOrgName
    ? (entity.clientOrgName ?? '—')
    : (entity.partnerOrgName ?? '—');

export const isoWeekToDate = (week?: number, year?: number): Date | undefined => {
  if (!week || !year) return undefined;
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dayOfWeek = simple.getDay();
  const monday = new Date(simple);
  if (dayOfWeek <= 4) {
    monday.setDate(simple.getDate() - dayOfWeek + 1);
  } else {
    monday.setDate(simple.getDate() + 8 - dayOfWeek);
  }
  return monday;
};

/** Inverz od isoWeekToDate — ISO tjedan+godina bilo kojeg datuma (tjedan
 * koji sadrži prvi četvrtak u godini je tjedan 1). Mora ostati algoritamski
 * usklađen s isoWeekToDate (vidi round-trip provjeru u testovima). */
export const dateToIsoWeek = (date: Date): { week: number; year: number } => {
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayNumber = (target.getDay() + 6) % 7; // Mon=0 ... Sun=6
  target.setDate(target.getDate() - dayNumber + 3); // četvrtak istog tjedna
  const isoYear = target.getFullYear();
  const jan4 = new Date(isoYear, 0, 4);
  const jan4DayNumber = (jan4.getDay() + 6) % 7;
  const week1Thursday = new Date(jan4);
  week1Thursday.setDate(jan4.getDate() - jan4DayNumber + 3);
  const week = 1 + Math.round((target.getTime() - week1Thursday.getTime()) / (7 * 86400000));
  return { week, year: isoYear };
};

/** Date → 'YYYY-MM-DD' u lokalnom vremenu (ne UTC, isto kao FormField.tsx Calendar helperi) —
 * format koji backend LocalDate polja (Contract.startDate/endDate) očekuju preko Jacksona. */
export const toIsoDateString = (date: Date): string => {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatWeekDate = (week?: number, year?: number) => {
  const date = isoWeekToDate(week, year);
  return date ? date.toLocaleDateString(currentLocale()) : undefined;
};

/** "tjedan/godina", isti format kao unos na FormWeekPickerInput. */
export const formatWeekYear = (week?: number, year?: number) =>
  week && year ? `${week}/${year}` : '—';

/** Tjedan/godina raspon (npr. "48/2026 — 12/2027") — usklađeno s prikazom
 * na formi za unos ugovora. */
export const formatWeekYearRange = (
  startWeek?: number,
  startYear?: number,
  endWeek?: number,
  endYear?: number
) => {
  const start = formatWeekYear(startWeek, startYear);
  const end = formatWeekYear(endWeek, endYear);
  if (start === '—' && end === '—') return '—';
  return `${start} — ${end}`;
};

/** Datum od–do ugovora (npr. "27.10.2025 – 08.03.2026") — contracts.start_date/end_date su
 * kanonski (stvarni datumi), tjedan/godina za prikaz se izvodi iz njih preko dateToIsoWeek. */
export const formatDateRange = (startIso?: string, endIso?: string) => {
  if (!startIso && !endIso) return '—';
  const start = startIso ? new Date(startIso).toLocaleDateString(currentLocale()) : '—';
  const end = endIso ? new Date(endIso).toLocaleDateString(currentLocale()) : '—';
  return `${start} – ${end}`;
};

/** Tjedan/godina raspon IZVEDEN iz stvarnih datuma (contracts.start_date/end_date), ne pamti se
 * zasebno — isti prikaz kao formatWeekYearRange, ali izvor su datumi. */
export const formatWeekYearRangeFromDates = (startIso?: string, endIso?: string) => {
  if (!startIso && !endIso) return '—';
  const start = startIso ? dateToIsoWeek(new Date(startIso)) : undefined;
  const end = endIso ? dateToIsoWeek(new Date(endIso)) : undefined;
  return formatWeekYearRange(start?.week, start?.year, end?.week, end?.year);
};

/** Naziv države na jeziku aplikacije (Deutschland / Njemačka / Germany) iz ISO koda — tablica
 * countries ima samo engleski naziv, koji ostaje fallback ako Intl ne poznaje kod. */
export const countryName = (code?: string, fallback?: string): string => {
  if (!code) return fallback ?? '';
  try {
    return (
      new Intl.DisplayNames([currentLocale()], { type: 'region' }).of(code) ?? fallback ?? code
    );
  } catch {
    return fallback ?? code;
  }
};

/** Opcije za odabir države — lokalizirani nazivi, poredani po abecedi tog jezika. */
export const buildCountryOptions = (countries?: { code?: string; name?: string }[]) =>
  (countries ?? [])
    .map((c) => ({ value: c.code ?? '', label: countryName(c.code, c.name) }))
    .sort((a, b) => a.label.localeCompare(b.label, currentLocale()));
